import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Container, Stack, Tab, Tabs, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useChildContext } from "../../contexts/ChildContext";
import { useRole } from "../../contexts/RoleContext";
import useChildName from "../../hooks/useChildName";
import { getTimelineEntriesSnapshot } from "../../services/timeline";
import DailyCareReportHeader, { DailyCareReportRangeSelector } from "../../components/Reports/DailyCareReportHeader";
import { MostLoggedAreasChart, SleepBehaviorTogetherChart } from "../../components/Reports/PatternsTrendCharts";
import {
  RANGE_PRESET,
  endOfDay,
  formatPeriodLabel,
  getMonthRange,
  getWeekRange,
  isValidDate,
  startOfDay,
  toEntryDate,
} from "../../components/Reports/reportBuilders/dailyCareReportEntryUtils";
import { ACTIVE_CHILD_STORAGE_KEY } from "../../components/Dashboard/shared/DashboardViewContext";
import ChildShellBottomNav from "../../components/Dashboard/shared/ChildShellBottomNav";
import CarePatternsSection from "../../features/patternDetection/CarePatternsSection";
import { detectCarePatterns } from "../../features/patternDetection/detectCarePatterns";
import { getEntryDate } from "../../components/Timeline/utils/medicationTimelinePatternObservationUtils";
import colors from "../../assets/theme/colors";

export const resolvePatternsChildId = ({
  routeChildId,
  selectedChildId,
  currentChildId,
  storedChildId,
  firstAccessibleChildId,
}) => routeChildId
  || currentChildId
  || selectedChildId
  || storedChildId
  || firstAccessibleChildId
  || "";

const PatternsTrendsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { childId } = useParams();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const { childrenWithAccess, getUserRoleForChild, loading: roleLoading } = useRole();
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [activeTab, setActiveTab] = useState("insights");
  const [rangePreset, setRangePreset] = useState(RANGE_PRESET.MONTH);
  const [customStartDate, setCustomStartDate] = useState(() => getMonthRange().start);
  const [customEndDate, setCustomEndDate] = useState(() => getMonthRange().end);

  const storedChildId = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY) || "";
  }, []);
  const effectiveChildId = resolvePatternsChildId({
    routeChildId: childId,
    selectedChildId: location.state?.selectedChildId,
    currentChildId,
    storedChildId,
    firstAccessibleChildId: childrenWithAccess?.[0]?.id,
  });
  const child = childrenWithAccess?.find((item) => item.id === effectiveChildId) || null;
  const viewerRole = getUserRoleForChild(effectiveChildId);
  const { childName, loading: childNameLoading, error } = useChildName(effectiveChildId);

  useEffect(() => {
    if (effectiveChildId && effectiveChildId !== currentChildId) {
      setCurrentChildId(effectiveChildId);
    }
  }, [effectiveChildId, currentChildId, setCurrentChildId]);

  const activeRange = useMemo(() => {
    if (rangePreset === RANGE_PRESET.MONTH) return getMonthRange();
    if (rangePreset === RANGE_PRESET.CUSTOM) return { start: customStartDate, end: customEndDate };
    return getWeekRange();
  }, [customEndDate, customStartDate, rangePreset]);

  const rangeStart = useMemo(() => startOfDay(activeRange.start), [activeRange]);
  const rangeEnd = useMemo(() => endOfDay(activeRange.end), [activeRange]);
  const isCustomRangeInvalid = !isValidDate(rangeStart) || !isValidDate(rangeEnd) || rangeStart > rangeEnd;
  const periodLabel = useMemo(
    () => formatPeriodLabel(rangeStart, rangeEnd, rangePreset),
    [rangeEnd, rangePreset, rangeStart]
  );
  const filteredEntries = useMemo(() => (
    entries.filter((entry) => {
      const date = toEntryDate(entry.timestamp || entry.date || entry.createdAt);
      return isValidDate(date) && date >= rangeStart && date <= rangeEnd;
    })
  ), [entries, rangeEnd, rangeStart]);
  const carePatterns = useMemo(() => detectCarePatterns({
    entries,
    viewerRole,
  }), [entries, viewerRole]);

  const loadEntries = useCallback(async () => {
    if (!effectiveChildId) return;
    setLoadingEntries(true);

    try {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const timelineEntries = await getTimelineEntriesSnapshot(effectiveChildId, {
        startDate: ninetyDaysAgo,
        endDate: new Date(),
        viewerRole,
      });
      setEntries(timelineEntries);
    } catch (loadError) {
      console.error("Error loading patterns entries:", loadError);
      setEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  }, [effectiveChildId, viewerRole]);

  useEffect(() => {
    if (effectiveChildId) {
      loadEntries();
    }
  }, [effectiveChildId, loadEntries]);

  if (roleLoading && !child) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!roleLoading && !child) {
    return <Navigate to="/dashboard" replace />;
  }

  if (childNameLoading) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Navigate to="/dashboard" replace />;
  }

  const navigateToDashboard = (target = "tools") => {
    navigate("/dashboard", {
      state: {
        selectedChildId: effectiveChildId || null,
        childShellTarget: target,
      },
    });
  };

  const handleChildShellNavSelect = (itemId) => {
    if (itemId === "home") {
      navigate("/home", { state: { selectedChildId: effectiveChildId || null } });
      return;
    }

    if (itemId === "tools") {
      navigateToDashboard("tools");
      return;
    }

    if (itemId === "profile") {
      navigate("/care-profile", {
        state: { childId: effectiveChildId || null },
      });
      return;
    }

    navigateToDashboard("today");
  };

  const handleViewPatternEntry = (entry) => {
    const entryDate = getEntryDate(entry);
    navigate("/dashboard", {
      state: {
        selectedChildId: effectiveChildId || null,
        childShellTarget: "today",
        selectedDate: entryDate?.toISOString?.() || null,
        timelineEntryId: entry.id || entry.entryId || null,
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background: `radial-gradient(circle at top left, ${alpha(colors.brand.ink, 0.08)} 0%, transparent 28%), linear-gradient(180deg, ${alpha(colors.brand.ice, 0.58)} 0%, ${theme.palette.background.paper} 100%)`,
        pb: { xs: "calc(5.8rem + env(safe-area-inset-bottom, 0px))", md: 3 },
      }}
    >
      <DailyCareReportHeader
        title="Patterns & trends"
        childName={childName || child?.name || "Care"}
        periodLabel={activeTab === "trends" ? "Care trends" : "Care insights"}
        rangePreset={rangePreset}
        setRangePreset={setRangePreset}
        customStartDate={customStartDate}
        setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}
        setCustomEndDate={setCustomEndDate}
        onBack={() => navigateToDashboard("tools")}
        showActions={false}
        showRangeControls={false}
        theme={theme}
      />

      <Container maxWidth={false} sx={{ maxWidth: "8.5in", px: { xs: 1.5, sm: 2.5 }, py: { xs: 2.2, md: 3 } }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Patterns and trends views"
          sx={{ mb: 2.4, borderBottom: `1px solid ${colors.semantic.line}`, "& .MuiTab-root": { minHeight: 48, fontWeight: 850, textTransform: "none" } }}
        >
          <Tab value="insights" label="Insights" />
          <Tab value="trends" label="Trends" />
        </Tabs>
        {loadingEntries ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isCustomRangeInvalid ? (
          <Alert severity="warning">Select a valid date range to view patterns.</Alert>
        ) : (
          activeTab === "insights" ? (
            <CarePatternsSection
              patterns={carePatterns}
              entries={entries}
              highlightedPatternId={location.state?.highlightedPatternId || ""}
              onViewEntry={handleViewPatternEntry}
            />
          ) : (
            <Stack spacing={2.4}>
              <DailyCareReportRangeSelector
                rangePreset={rangePreset}
                setRangePreset={setRangePreset}
                customStartDate={customStartDate}
                setCustomStartDate={setCustomStartDate}
                customEndDate={customEndDate}
                setCustomEndDate={setCustomEndDate}
                periodLabel={periodLabel}
              />
              <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.9rem", lineHeight: 1.55 }}>
                Charts summarize what was recorded. They do not show cause and effect.
              </Typography>
              <SleepBehaviorTogetherChart entries={filteredEntries} theme={theme} />
              <MostLoggedAreasChart entries={filteredEntries} theme={theme} />
            </Stack>
          )
        )}
      </Container>
      <ChildShellBottomNav
        activeItem="tools"
        onSelect={handleChildShellNavSelect}
      />
    </Box>
  );
};

export default PatternsTrendsPage;
